import User from '@models/User'
import * as apiClient from '@utils/apiClient/auth'
import { UpdateEmailParams } from '@utils/apiClient/auth'
import { AuthorizationUserType } from '@utils/authorizationUserType'
import Cognito from '@utils/Cognito'
import { makeAutoObservable, runInAction } from 'mobx'

export default class AuthStore {
  private _shouldRegisterEmail = false
  user: User | null = null
  isLoading = true
  emailForForgotPassword = ''
  userAttributes: any = null

  get isLoggedIn() {
    return !!this.user
  }

  get userId() {
    return this.user?.id
  }

  get isTemporaryRegistration() {
    return this.userAttributes !== null
  }

  get shouldRegisterEmail() {
    return this._shouldRegisterEmail
  }

  // Constructor ------------

  constructor() {
    makeAutoObservable(this)
    this.getUser()
  }

  // Actions ---------------

  getUser = async () => {
    const userIdStr = localStorage.getItem('userId')
    let userId = userIdStr ? Number(userIdStr) : void 0
    if (userId && isNaN(userId)) userId = void 0

    try {
      const { user } = await apiClient.getUser(userId)
      if (!user) return

      const hasEmail = !userId ? await Cognito.hasEmailAddress() : true

      runInAction(() => {
        this.user = new User(user)
        this._shouldRegisterEmail = !hasEmail
      })
    } catch (e) {
      console.log(e)
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  logIn = async (email: string, password: string, userCode?: string) => {
    const type = userCode ? AuthorizationUserType.Staff : AuthorizationUserType.User

    // Cognito で認証する必要があるが、入居者コードが必要なため、先に API でログインしてユーザー情報を取得する
    const { code, user } = await apiClient.login({
      type,
      email,
      loginCode: email,
      password,
      userCode,
    })

    const res = await (async () => {
      if (type === AuthorizationUserType.Staff) {
        return await Cognito.logIn(`${type}_${email}`, password)
      }

      // 一般ユーザー
      let code = user.code
      if (/^\d+$/.test(code)) code = code.slice(0, -1) // 3桁物件は末尾の数字(請求区分)を削除

      return await Cognito.logIn(`${type}_${code}`, password)
    })()

    runInAction(() => {
      if (type === AuthorizationUserType.Staff) localStorage.setItem('userId', String(user.id))
      if (res.isTemporary) this.userAttributes = res.data
      if (code & apiClient.UserResponseCode.EmailRequired) this._shouldRegisterEmail = true
      this.user = new User(user)
    })
  }

  logOut = () => {
    Cognito.logOut()
    this.user = null
    this.userAttributes = null
  }

  sendResetPasswordEmail = async (email: string) => {
    this.emailForForgotPassword = email
    await apiClient.sendResetPasswordEmail({ email })
  }

  confirmPassword = async (params: { email: string; code: string; password: string }) => {
    await apiClient.confirmPassword(params)
  }

  changeTemporaryPassword = async (newPassword: string) => {
    await Cognito.changeTemporaryPassword(newPassword, this.userAttributes)
    this.userAttributes = null
  }

  changePassword = async ({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }) => {
    await Cognito.changePassword(currentPassword, newPassword)
  }

  updateEmail = async (params: UpdateEmailParams) => {
    await apiClient.updateEmail(params)
    this._shouldRegisterEmail = false
  }
}
